// Negotiate — leverage analysis for a saved offer.
//
// Phase 14g: payload to salary_script is now the full Offer object enriched
// with (a) salary benchmark from useSalaryBenchmark cache, (b) other offers
// from localStorage as BATNA signals, (c) optional CV proof point. Backend
// produces a structured 5-section markdown package: counter-anchor, email,
// verbal script, trade-offs, risks.

import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LogoSquare from '../components/ui/LogoSquare';
import Progress from '../components/ui/Progress';
import MarketBar from '../components/domain/MarketBar';
import { useJobRunner } from '../lib/useJobRunner';
import { useSalaryBenchmark } from '../lib/useSalaryBenchmark';
import { cleanLLMOutput } from '../lib/clean-llm-output';
import type { Offer } from '../lib/types';

export default function Negotiate() {
  const { offerId } = useParams();
  const nav = useNavigate();
  // Reattach: salary_script payload contains the offer company. Local
  // offerId is a client uuid the server doesn't know about, so we
  // narrow by company — close enough since users don't have two
  // simultaneous negotiate runs against the same employer.
  const localOfferCompany = useMemo<string>(() => {
    try {
      const raw = localStorage.getItem('nice2job.offers');
      const list = raw ? (JSON.parse(raw) as Array<{ id: string; company?: string }>) : [];
      return (list.find((o) => o.id === offerId)?.company || '').toLowerCase();
    } catch { return ''; }
  }, [offerId]);
  const runner = useJobRunner(`negotiate-${offerId ?? 'none'}`, {
    reattach: {
      cmd: 'salary_script',
      match: (p) => !!localOfferCompany && String(p.company || '').toLowerCase() === localOfferCompany,
    },
  });

  const allOffers = useMemo<Offer[]>(() => {
    try {
      const raw = localStorage.getItem('nice2job.offers');
      return raw ? (JSON.parse(raw) as Offer[]) : [];
    } catch { return []; }
  }, []);

  const offer = useMemo(() => allOffers.find((o) => o.id === offerId), [allOffers, offerId]);

  // Hooks must be called unconditionally — call useSalaryBenchmark with a
  // safe shim if the offer is missing, then short-circuit the render below.
  const safeOffer: Offer = offer ?? {
    id: offerId || 'missing',
    company: '', role: '', base: 0, equity: 0, bonus: 0, total: 0,
    scores: { mission: 0, growth: 0, culture: 0, comp: 0, pace: 0, remote: 0 },
  };
  const bench = useSalaryBenchmark(safeOffer);

  if (!offer) {
    return (
      <div className="page-enter">
        <PageHeader eyebrow="Offer not found" title="We can't find this offer." sub="Back to the list." />
        <button className="btn" onClick={() => nav('/offers')}>← Offers</button>
      </div>
    );
  }

  // Build the rich payload for salary_script. Includes cached benchmark
  // (no extra LLM call here) + every OTHER offer as BATNA. Backend uses
  // this to size the counter-anchor honestly.
  const otherOffers = allOffers
    .filter((o) => o.id !== offer.id)
    .map((o) => ({ company: o.company, total: o.total }));

  const start = () => {
    runner.startJob('salary_script', {
      company: offer.company,
      role: offer.role,
      location: offer.location || '',
      base: offer.base,
      equity: offer.equity,
      bonus: offer.bonus,
      total: offer.total,
      benchmark: bench.benchmark
        ? {
            p25: bench.benchmark.p25,
            p50: bench.benchmark.p50,
            p75: bench.benchmark.p75,
            confidence: bench.benchmark.confidence,
            reasoning: bench.benchmark.reasoning,
          }
        : null,
      otherOffers,
    });
  };

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/offers')}>← Offers</button>
      <PageHeader
        eyebrow={`Negotiate · ${offer.company}`}
        title="Leverage, mapped."
        sub="Counter-anchor, email, verbal script, trade-offs, risks. Built from your offer + estimated market gap. Runs as a background job — close this page if you want, it'll keep going."
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20, alignItems: 'start' }}>
        <div className="card" style={{ padding: 22 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
            <LogoSquare name={offer.company} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{offer.company}</div>
              <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{offer.role}</div>
            </div>
          </div>
          <div className="hair" style={{ marginBottom: 16 }} />
          <div className="num" style={{ fontSize: 34 }}>${offer.total}k</div>
          <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
            Base ${offer.base}k · Equity ${offer.equity}k · Bonus ${offer.bonus}k
          </div>

          {/* Market bar — same component used in Offers cards. The user
              can refresh the benchmark from here if it's stale or missing. */}
          <MarketBar
            totalK={offer.total}
            benchmark={bench.benchmark}
            isLoading={bench.isLoading}
            isStale={bench.isStale}
            error={bench.error}
            onFetch={bench.fetch}
            onRefresh={bench.benchmark ? bench.fetch : undefined}
          />

          {otherOffers.length > 0 && (
            <div style={{ marginTop: 16, padding: '10px 12px', background: 'rgba(60, 150, 90, 0.05)', border: '1px solid rgba(60, 150, 90, 0.25)', borderRadius: 6, fontSize: 11.5, color: 'var(--ink2)' }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>BATNA on file ({otherOffers.length})</div>
              {otherOffers.map((o, i) => (
                <div key={i} style={{ color: 'var(--ink3)' }}>
                  · {o.company || 'unknown'} · ${o.total}k
                </div>
              ))}
              <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 10.5 }}>
                These will inform the leverage frame in the script.
              </div>
            </div>
          )}

          <button
            className="btn accent lg"
            style={{ width: '100%', marginTop: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            disabled={runner.isRunning}
            onClick={start}
            title={bench.benchmark ? 'Drafts script using market benchmark above' : 'No market benchmark yet — drafts using soft signals only. Click "Estimate market" first for sharper anchors.'}
          >
            <Sparkles size={14} />
            {runner.isRunning ? 'Drafting…' : runner.status === 'done' ? 'Regenerate' : 'Draft negotiation package'}
          </button>
          {runner.isActive && (
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink3)' }}>
              Job {runner.jobId?.slice(0, 8)} · status: {runner.status}
            </div>
          )}
          {runner.errorLine && (
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--bad)' }}>{runner.errorLine}</div>
          )}
        </div>

        <div className="card" style={{ padding: 22, minHeight: 400 }}>
          <div className="tab" style={{ marginBottom: 10 }}>Negotiation package</div>
          {!runner.isActive && (
            <div className="empty" style={{ padding: '32px 12px' }}>
              <span className="serif">Ready when you are.</span>
              {bench.benchmark
                ? 'Click Draft to build a counter-anchor email, verbal script, and trade-off analysis tailored to your market gap.'
                : 'Tip: estimate the market rate first (left panel) so the counter-anchor is grounded — otherwise the script falls back to soft signals only.'}
            </div>
          )}
          {runner.isActive && (() => {
            const clean = cleanLLMOutput(runner.log || '');
            const rawLen = (runner.log || '').length;
            return (
              <>
                {/* Always-visible streaming indicator while running so the
                    user sees progress even during the first 1-2s when only
                    orchestration noise (which cleanLLMOutput strips) has
                    arrived and the markdown panel is still empty. */}
                {runner.isRunning && (
                  <>
                    <Progress value={Math.min(95, rawLen / 30)} />
                    <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4, marginBottom: 12 }}>
                      Streaming · {rawLen.toLocaleString()} chars
                    </div>
                  </>
                )}
                {clean ? (
                  <div
                    className="report-md"
                    style={{
                      fontSize: 13.5,
                      color: 'var(--ink)',
                      lineHeight: 1.7,
                      maxHeight: 700,
                      overflowY: 'auto',
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{clean}</ReactMarkdown>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--ink3)', fontStyle: 'italic' }}>
                    {runner.isRunning ? 'Warming up…' : ''}
                  </div>
                )}
              </>
            );
          })()}
          {runner.status === 'done' && (
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button className="btn" onClick={runner.dismiss}>Clear</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
